import type { IttCartItem } from '@hyundai/shared-lib';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { isNotNil, pickBy } from 'es-toolkit';
import { type ApiTypes } from '../../..';
import type { Loadable, Loaded } from '../../guards/ngrx-loader.guard';
import { vehicleActions } from './vehicle.actions';

export type HistoryFilter = ApiTypes.FilterObject;
export type HistoryEntries = Partial<Record<keyof ApiTypes.FilterObject, string>>;
export interface QuickfilterEntry {
    name: string;
    filter: string;
}
interface VehicleState {
    numberOfVehicles: Loadable<number>;
    list: Loadable<ApiTypes.VehicleList>;
    favorites: Loadable<ApiTypes.Favorites>;
    favoritesLoading: boolean;
    similarList: Loadable<ApiTypes.VehicleList>;
    detail: Loadable<ApiTypes.VehicleDetail>;
    similarListLoading: boolean;
    listLoading: boolean;
    cart: IttCartItem[];
    filterInputs: Loadable<ApiTypes.VehicleFilterInputs>;
    filterSelected: ApiTypes.FilterObject;
    filterSelectedPreview: ApiTypes.FilterObject;
    filterPreviewNumberOfVehicles: Loadable<number>;
    filterLoadingPreviewNumberOfVehicle: boolean;
    trackingKeys: Record<string, boolean>;
    trackingKeysPreview: Record<string, boolean>;
    socialProof: Loadable<ApiTypes.SocialProofResponse>;
    currentHistoryEntries: Partial<Record<keyof ApiTypes.FilterObject, string>>;
    filterHistorys: QuickfilterEntry[];
    listPosition: number | null;
}

export const initialState: VehicleState = {
    numberOfVehicles: null,
    list: null,
    similarList: null,
    detail: null,
    similarListLoading: false,
    listLoading: false,
    cart: [],
    filterInputs: null,
    filterSelected: {},
    filterSelectedPreview: {},
    filterLoadingPreviewNumberOfVehicle: false,
    filterPreviewNumberOfVehicles: null,
    trackingKeys: {},
    trackingKeysPreview: {},
    socialProof: {
        error: false,
        showViews: false,
        totalViews: 0,
        roundedViews: 0,
    },
    currentHistoryEntries: {},
    filterHistorys: [],
    listPosition: null,
    favorites: null,
    favoritesLoading: false,
};

export const vehicleState = createFeature({
    name: 'vehicle-market',
    reducer: createReducer(
        initialState,
        on(vehicleActions.init, (state, { initalFilter }): VehicleState => {
            return {
                ...state,
                filterSelected: { ...initalFilter },
                filterSelectedPreview: { ...initalFilter },
            };
        }),

        on(vehicleActions.filter, (state): VehicleState => {
            return {
                ...state,
                filterSelected: { ...state.filterSelectedPreview },
                trackingKeys: { ...state.trackingKeysPreview },
                trackingKeysPreview: {},
            };
        }),
        on(vehicleActions.resetFilter, (state): VehicleState => {
            return {
                ...state,
                filterSelected: {},
                filterSelectedPreview: {},
                filterPreviewNumberOfVehicles: null,
            };
        }),
        on(vehicleActions.resetTrackingKeys, (state): VehicleState => {
            return {
                ...state,
                trackingKeysPreview: {},
                trackingKeys: {},
            };
        }),

        on(vehicleActions.loadVehicleList, (state): VehicleState => ({ ...state, listLoading: true })),
        on(
            vehicleActions.loadVehicleListSuccess,
            (state, { list }): VehicleState => ({ ...state, list, listLoading: false }),
        ),
        on(
            vehicleActions.loadVehicleListFailure,
            (state): VehicleState => ({ ...state, list: false, listLoading: false }),
        ),

        on(vehicleActions.loadNumberOfVehicles, (state): VehicleState => ({ ...state, numberOfVehicles: null })),
        on(
            vehicleActions.loadNumberOfVehiclesSuccess,
            (state, { count }): VehicleState => ({ ...state, numberOfVehicles: count }),
        ),
        on(
            vehicleActions.loadNumberOfVehiclesFailure,
            (state): VehicleState => ({ ...state, numberOfVehicles: false }),
        ),
        on(vehicleActions.loadVehicleDetail, (state): VehicleState => ({ ...state, detail: null })),
        on(
            vehicleActions.loadVehicleDetailSuccess,
            (state, { vehicle }): VehicleState => ({ ...state, detail: vehicle }),
        ),
        on(vehicleActions.loadVehicleDetailFailure, (state): VehicleState => ({ ...state, detail: false })),
        on(
            vehicleActions.loadSimilarVehicleListSuccess,
            (state, { list }): VehicleState => ({ ...state, similarList: list, similarListLoading: false }),
        ),
        on(
            vehicleActions.loadSimilarVehicleListFailure,
            (state): VehicleState => ({ ...state, similarList: false, similarListLoading: false }),
        ),
        on(
            vehicleActions.loadFavorites,
            (state): VehicleState => ({ ...state, favorites: null, favoritesLoading: true }),
        ),
        on(
            vehicleActions.loadFavoritesSuccess,
            (state, { favorites }): VehicleState => ({ ...state, favorites, favoritesLoading: false }),
        ),
        on(
            vehicleActions.loadFavoritesFailure,
            (state): VehicleState => ({ ...state, favorites: false, favoritesLoading: false }),
        ),
        on(vehicleActions.removeFavoriteVehicle, (state, { slug }): VehicleState => {
            const favorites = state.favorites;
            if (favorites === null || favorites === false) {
                return state;
            }

            const vehicles = favorites.vehicles.filter((vehicle) => vehicle.slug !== slug);
            return {
                ...state,
                favorites: {
                    ...favorites,
                    count: vehicles.length,
                    vehicles,
                },
            };
        }),

        on(vehicleActions.loadFilter, (state): VehicleState => ({ ...state, filterInputs: null })),
        on(vehicleActions.loadFilterSuccess, (state, { filterInputs }): VehicleState => ({ ...state, filterInputs })),
        on(vehicleActions.loadFilterFailure, (state): VehicleState => ({ ...state, filterInputs: false })),

        on(vehicleActions.addFilter, (state, { filterObject, trackingKeys, historyMessage }): VehicleState => {
            const filterSelectedPreview = pickBy(
                {
                    ...state.filterSelectedPreview,
                    ...filterObject,
                },
                (value) => isNotNil(value) && (!Array.isArray(value) || value.length > 0),
            ) as ApiTypes.FilterObject;

            return {
                ...state,
                filterSelectedPreview,
                filterLoadingPreviewNumberOfVehicle: true,
                trackingKeysPreview: {
                    ...state.trackingKeysPreview,
                    ...trackingKeys.reduce<Record<string, boolean>>((prev, cur) => {
                        return {
                            ...prev,
                            [cur]: true,
                        };
                    }, {}),
                },
                currentHistoryEntries: historyMessage
                    ? { ...state.currentHistoryEntries, ...historyMessage }
                    : state.currentHistoryEntries,
            };
        }),
        on(vehicleActions.removeFilter, (state, { removeFilterObject, trackingKeys }): VehicleState => {
            return {
                ...state,
                filterSelectedPreview: (Object.keys(state.filterSelectedPreview) as Array<keyof ApiTypes.FilterObject>)
                    .filter((key) => !removeFilterObject.includes(key))
                    .reduce((obj, key) => {
                        return {
                            ...obj,
                            [key]: state.filterSelectedPreview[key],
                        } satisfies ApiTypes.FilterObject;
                    }, {} satisfies ApiTypes.FilterObject),
                currentHistoryEntries: (Object.keys(state.currentHistoryEntries) as Array<keyof ApiTypes.FilterObject>)
                    .filter((key) => !removeFilterObject.includes(key))
                    .reduce((obj, key) => {
                        return {
                            ...obj,
                            [key]: state.currentHistoryEntries[key],
                        } satisfies ApiTypes.FilterObject;
                    }, {} satisfies ApiTypes.FilterObject),
                filterLoadingPreviewNumberOfVehicle: true,
                trackingKeysPreview: {
                    ...state.trackingKeysPreview,
                    ...trackingKeys.reduce<Record<string, boolean>>((prev, cur) => {
                        return {
                            ...prev,
                            [cur]: false,
                        };
                    }, {}),
                },
            };
        }),
        on(vehicleActions.resetPreviewFilter, (state): VehicleState => {
            return {
                ...state,
                filterSelectedPreview: state.filterSelected,
                filterPreviewNumberOfVehicles: null,
                trackingKeysPreview: {},
            };
        }),

        on(
            vehicleActions.loadFilterPreviewNumberOfVehiclesSuccess,
            (state, { count }): VehicleState => ({
                ...state,
                filterPreviewNumberOfVehicles: count,
                filterLoadingPreviewNumberOfVehicle: false,
            }),
        ),
        on(
            vehicleActions.loadFilterPreviewNumberOfVehiclesFailure,
            (state): VehicleState => ({
                ...state,
                filterPreviewNumberOfVehicles: false,
                filterLoadingPreviewNumberOfVehicle: false,
            }),
        ),
        on(vehicleActions.setAccessoryCart, (state, { newCart }): VehicleState => {
            return {
                ...state,
                cart: newCart,
            };
        }),
        on(
            vehicleActions.loadSocialProofSuccess,
            (state, payload): VehicleState => ({
                ...state,
                socialProof: payload,
            }),
        ),
        on(vehicleActions.setHistorySuccess, (state, { historys }): VehicleState => {
            return {
                ...state,
                filterHistorys: historys,
                currentHistoryEntries: {},
            };
        }),
        on(vehicleActions.setVehicleListPosition, (state, { position }): VehicleState => {
            return {
                ...state,
                listPosition: Math.round(position),
            };
        }),
        on(vehicleActions.resetVehicleListPosition, (state): VehicleState => {
            return {
                ...state,
                listPosition: null,
            };
        }),
    ),

    extraSelectors: (baseSelectors) => {
        // LIST
        const selectLoadedList = createSelector(
            baseSelectors.selectList,
            (list) => list as Loaded<ApiTypes.VehicleList>,
        );
        const selectIsLoadingList = createSelector(baseSelectors.selectList, (list) => list === null);

        // Filter
        const selectLoadedFilterInputs = createSelector(
            baseSelectors.selectFilterInputs,
            (filter) => filter as Loaded<ApiTypes.VehicleFilterInputs>,
        );

        // DETAIL
        const selectLoadedDetail = createSelector(
            baseSelectors.selectDetail,
            (detail) => detail as Loaded<ApiTypes.VehicleDetail>,
        );
        const selectIsLoadingDetail = createSelector(baseSelectors.selectDetail, (detail) => detail === null);

        // FAVORITES
        const selectLoadedFavorites = createSelector(
            baseSelectors.selectFavorites,
            (favorites) => favorites as Loaded<ApiTypes.VehicleList>,
        );
        const selectIsLoadingFavorites = createSelector(
            baseSelectors.selectFavorites,
            (favorites) => favorites === null,
        );

        // SIMILAR LIST
        const selectLoadedSimilarList = createSelector(
            baseSelectors.selectSimilarList,
            (list) => list as Loaded<ApiTypes.VehicleList>,
        );
        const selectIsLoadingSimilarList = createSelector(baseSelectors.selectSimilarList, (list) => list === null);

        // NUMBER OF VEHICLES
        const selectIsLoadingNumberOfVehicles = createSelector(
            baseSelectors.selectNumberOfVehicles,
            (count) => count === null,
        );
        const selectLoadedNumberOfVehicles = createSelector(
            baseSelectors.selectNumberOfVehicles,
            (count) => count as Loaded<number>,
        );
        const selectSortOptions = createSelector(baseSelectors.selectFilterInputs, (inputs) => {
            if (!inputs) {
                return;
            }
            return inputs.sorting.values.map((s) => ({
                label: s.label,
                value: JSON.stringify({ key: s.key, order: s.order }),
            }));
        });
        const selectLoadedSocialProof = createSelector(
            baseSelectors.selectSocialProof,
            (socialProof) => socialProof as Loaded<ApiTypes.SocialProofResponse>,
        );
        const selectIsLoadingSocialProof = createSelector(
            baseSelectors.selectSocialProof,
            (socialProof) => socialProof === null,
        );

        return {
            selectLoadedList,
            selectIsLoadingList,
            selectIsLoadingNumberOfVehicles,
            selectLoadedNumberOfVehicles,
            selectIsLoadingDetail,
            selectLoadedDetail,
            selectLoadedSimilarList,
            selectIsLoadingSimilarList,
            selectLoadedFavorites,
            selectIsLoadingFavorites,
            selectSortOptions,
            selectLoadedFilterInputs,
            selectLoadedSocialProof,
            selectIsLoadingSocialProof,
        };
    },
});
